/*
 * Copyright (c) 2024-2025 Nodals AI Ltd
 * All rights reserved.
 */

import { EventDispatcher } from '../events';
import { Logger } from '../logger';
import { Engine } from '../rules/engine';
import { AdIdType, CampaignType } from '../types/index';
import { ViewManager } from './activity';
import { Ad } from './ad';
import { BrandSafety, BrandSafetyEvents, BrandSafetyManager } from './advertiser';

import type { Facts } from '../rules';
import type { AdAssignedKeyValues, AdData, CampaignData, KeyValue } from '../types/index';
import type { HierarchyNode } from './activity';

const logger = Logger.getInstance();

class Campaign extends EventDispatcher<BrandSafetyEvents> {
  private ads: Ad[] = [];
  private data: CampaignData;
  private brandSafety: BrandSafety | null = null;
  private activeBrandViolation: boolean = false;
  private brandSafetyManager: BrandSafetyManager = BrandSafetyManager.getInstance();

  constructor(campaignData: CampaignData, facts: Facts) {
    super();
    this.data = { ...campaignData };
    this.ads = this.data.ads.map((adData) => new Ad(this.mergeRules(adData), facts));
    this.setViewsFact();
  }

  public get adDeliveryIds(): string[] {
    return this.ads.map((ad) => ad.deliveryId);
  }

  public get id(): string {
    return this.data.id;
  }

  public get type(): CampaignType {
    return this.data.type;
  }

  public get deals(): string[] {
    return this.data?.deals || [];
  }

  public get pageIsBrandCompliant(): boolean {
    return !this.activeBrandViolation;
  }

  public get linkedExternalLineIds(): string[] {
    const linkedLineIds = Array.from(
      new Set(
        this.ads
          .map((ad) => ad.linkedLineId)
          .filter((id): id is string => id !== null && id !== undefined)
      )
    );
    return linkedLineIds;
  }

  public getMatchingAdDeliveryId(lineId: string): string | undefined {
    const matchingAd = this.ads.find((ad) => ad.linkedLineId === lineId);
    if (matchingAd) {
      return matchingAd.deliveryId;
    }
  }

  public getEligibleAdIds(): string[] {
    if (this.activeBrandViolation) {
      return [];
    }
    const filteredAds = this.ads.filter((ad) => ad.isEligible());
    return filteredAds.map((ad) => ad.deliveryId);
  }

  public hasEligibleAds(): boolean {
    return this.ads.some((ad) => ad.isEligible());
  }

  public get keyValuesByAd(): AdAssignedKeyValues[] {
    return this.ads.map((ad) => ({
      id: ad.deliveryId,
      kvs: ad.keyValues,
    }));
  }

  public get keyValues(): KeyValue[] {
    const kvs: KeyValue[] = [];
    this.ads.forEach((ad) => {
      logger.debug(`Retrieved key-values for ad ${ad.deliveryId}: ${JSON.stringify(ad.keyValues)}`);
      ad.keyValues.forEach((kv) => {
        const existingKv = kvs.find((existingKv) => existingKv.key === kv.key);
        if (!existingKv) {
          kvs.push({ key: kv.key, value: this.activeBrandViolation ? '' : kv.value });
        } else {
          if (this.activeBrandViolation) {
            return;
          }
          const existingValues = existingKv.value.split(',');
          if (!existingValues.includes(kv.value)) {
            existingKv.value += `,${kv.value}`;
          }
        }
      });
    });
    if (this.activeBrandViolation && kvs.length === 0) {
      // This is required because of the Prebid issue where if no key-values are set
      // it will use the last known key-values for the ad unit.
      // Horrible, hack - will have to contribute a fix to Prebid at some point.
      kvs.push({ key: 'nodals', value: '' });
    }
    return kvs;
  }

  public set adKeyValues(adAssignedKvs: AdAssignedKeyValues[]) {
    if ([CampaignType.LEARNING_DIRECT, CampaignType.LEARNING_PROGRAMMATIC].includes(this.type)) {
      // Never set key values for learning campaigns
      return;
    }
    adAssignedKvs.forEach((adAssignedKv) => {
      const ad = this.ads.find((a) => a.deliveryId === adAssignedKv.id);
      if (ad) {
        ad.keyValues = adAssignedKv.kvs ?? [];
      }
    });
  }

  public clearAdKeyValues(): void {
    this.ads.forEach((ad) => {
      ad.keyValues = [];
    });
  }

  public getAdKeyValues(id: string, type: AdIdType): KeyValue[] {
    logger.info(`Fetching key values for reporting ad with ID: ${id} of type: ${type}`);
    const matchingAd = this.ads.find((ad) =>
      type === AdIdType.EXTERNAL_ID ? ad.linkedLineId === id : ad.deliveryId === id
    );
    return matchingAd ? matchingAd.keyValues : [];
  }

  public isAssociatedWith(externalId: string): boolean {
    logger.debug(`Checking campaign association with ad server line external ID: ${externalId}`);
    return this.ads.some((ad) => ad.linkedLineId === externalId);
  }

  public enableBrandSafetyMonitoring(contentSelectors?: string): void {
    if (!this.data.bkwds || this.brandSafety) {
      return;
    }

    try {
      this.brandSafety = this.brandSafetyManager.getBrandSafetyForCampaign(
        this.id,
        this.data.bkwds,
        contentSelectors
      );

      if (this.brandSafety) {
        this.brandSafety.on('BrandSafetyComplianceViolation', (event) => {
          if (!this.activeBrandViolation) {
            logger.debug(
              `Brand safety violation detected for campaign ${this.id}: ${JSON.stringify(event.data.detectedWords)}`
            );
            this.activeBrandViolation = true;
            this.dispatch('BrandSafetyComplianceViolation', event.data);
          }
        });

        this.brandSafety.on('BrandSafetyComplianceRestored', (event) => {
          if (this.activeBrandViolation) {
            logger.debug(`Brand safety compliance restored for campaign ${this.id}`);
            this.activeBrandViolation = false;
            this.dispatch('BrandSafetyComplianceRestored', event.data);
          }
        });

        const currentState = this.brandSafety.getCurrentViolationState();
        if (currentState.hasViolation && !this.activeBrandViolation) {
          logger.debug(
            `Syncing existing brand safety violation for campaign ${this.id}: ${JSON.stringify(currentState.detectedWords)}`
          );
          this.activeBrandViolation = true;
          this.dispatch('BrandSafetyComplianceViolation', {
            detectedWords: currentState.detectedWords || [],
          });
        }
      }
    } catch (error) {
      logger.error(`Error enabling brand safety monitoring for campaign ${this.id}: ${error}`);
      this.brandSafety = null;
    }
  }

  public disableBrandSafetyMonitoring(): void {
    if (this.brandSafety) {
      this.brandSafety = null;
      logger.info(`Brand safety monitoring disabled for campaign ${this.id}`);
    }
  }

  private setViewsFact(): void {
    const hierarchy: HierarchyNode = {
      id: this.id,
      children: this.ads.map((ad) => ({ id: ad.deliveryId, children: [] })),
    };
    const campaignViews = ViewManager.getViewsForEntity(this.id, hierarchy);
    this.ads.forEach((ad) => {
      ad.addFacts({ 'campaign.views': campaignViews });
    });
  }

  private mergeRules(adData: AdData): AdData {
    if (!this.data.rules) {
      return adData;
    }

    const mergedAdData = { ...adData };
    const baseConditions = mergedAdData?.rules?.conditions || {};
    const additionalConditions = this.data?.rules?.conditions || {};

    mergedAdData.rules = {
      conditions: Engine.mergeConditions(baseConditions, additionalConditions),
    };

    return mergedAdData;
  }

  public destroy(): void {
    this.disableBrandSafetyMonitoring();
  }
}

export { Campaign };
